'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDown, MinusIcon, PlusIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    hasPlusIcon?: boolean
  }
>(({ className, children, hasPlusIcon, ...props }, ref) => (
  <AccordionPrimitive.Header className='flex'>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]_.trigger-minus]:visible [&[data-state=open]_.trigger-plus]:invisible [&[data-state=open]_svg]:rotate-180',
        className
      )}
      {...props}
    >
      {children}
      {!hasPlusIcon ? (
        <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200' />
      ) : (
        <span className='relative h-4 w-4'>
          <MinusIcon className='trigger-minus invisible absolute left-0 top-0 h-4 w-4 shrink-0 transition-transform duration-200' />
          <PlusIcon className='trigger-plus absolute left-0 top-0 h-4 w-4 shrink-0 transition-transform duration-200' />
        </span>
      )}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className='overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
    {...props}
  >
    <div className={cn('pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
